import { registerDecorators as _registerDecorators, registerComponent as _registerComponent, LightningElement } from "lwc";
import _tmpl from "./typeAhead.html";
import { selectParams, WireStore } from '@oneappexchange/lwc-wired';
import { APPX_SITE_URL } from '~/modules/config';
import { Routes } from '~/modules/routes';
import { i18nStore, selectCurrentLanguage, selectTypeAhead, WireI18n } from '~/modules/stores/i18nStore';
import { recentItemStore, selectRecentItems } from '~/modules/stores/recentItemStore';
import { navigate, routerStore, selectUrl } from '~/modules/stores/routerStore';
import { searchTermSuggestionStore, selectSearchTermSuggestions, selectSearchTermSuggestionsFromTrie } from '~/modules/stores/searchTermSuggestionStore';
import { siteConfigStore } from '~/modules/stores/siteConfigStore';
import { selectTopSearches, topSearchesStore } from '~/modules/stores/topSearchesStore';
import { debounce } from '~/modules/utilities/browser';
import { sanitizeText } from '~/modules/utilities/html_utilities';
import { ENGLISH_LANGUAGE_CODE, JAPANESE_LANGUAGE_CODE } from '~/modules/utilities/language_utils';
var FocusDirection = /*#__PURE__*/function (FocusDirection) {
  FocusDirection[FocusDirection["Up"] = -1] = "Up";
  FocusDirection[FocusDirection["Down"] = 1] = "Down";
  return FocusDirection;
}(FocusDirection || {});
// This should be configurable in the future
const debounceTimeout = 300;
const PLACEHOLDER_DISPLAY_DURATION = 3000; // Interval between placeholder rotations (ms)
const PLACEHOLDER_ANIMATION_DURATION = 700; // Roll animation duration (ms)
const SCROLL_TOP_THRESHOLD = 80; // Pixels from top to consider "at top" for placeholder animation
const MIN_CHARACTERS_BEFORE_RETURNING_RESULTS = 3;
class TypeAhead extends LightningElement {
  constructor(...args) {
    super(...args);
    this.keyword = '';
    this.userId = 'testuser';
    this.displayDrawer = false;
    this.maxSearchCharLimit = 512;
    this.isInputFocused = false;
    // Animated placeholder state
    this.currentPlaceholderIndex = 0;
    this.isRolling = false;
    // @ts-expect-error * Automated Type Error Fix
    this.boundScrollHandler = this.handleScroll.bind(this);
    this.searchTermSuggestions = [];
    this.recentItems = [];
    this.topSearches = [];
    // @ts-expect-error * Automated Type Error Fix
    this.labels = {
      searchBar: '',
      terms: '',
      recentItems: '',
      topSearches: '',
      placeholders: '',
      focusedPlaceholder: '',
      clearSearchButton: '',
      submitSearchButton: ''
    };
  }
  get isAgenticSearchEnabled() {
    return true;
  }
  get placeholderLabels() {
    if (!this.labels?.placeholders) {
      return [];
    }
    return this.labels.placeholders.split(';').filter(Boolean);
  }
  connectedCallback() {
    if (this.isAgenticSearchEnabled && this.isOnHomePage) {
      if (typeof window !== 'undefined' && window.scrollY <= SCROLL_TOP_THRESHOLD) {
        this.startPlaceholderAnimation();
      }
      window.addEventListener('scroll', this.boundScrollHandler, {
        passive: true
      });
    }
  }
  disconnectedCallback() {
    if (this.isAgenticSearchEnabled) {
      window.removeEventListener('scroll', this.boundScrollHandler);
    }
    this.stopPlaceholderAnimation();
  }
  get isOnHomePage() {
    const url = selectUrl(routerStore.getState());
    return url === Routes.HOME || url === '' || url === 'jp';
  }
  handleScroll() {
    const atTop = typeof window !== 'undefined' && window.scrollY <= SCROLL_TOP_THRESHOLD;
    if (this.isOnHomePage && atTop && this.isAgenticSearchEnabled && !this.placeholderIntervalId) {
      this.startPlaceholderAnimation();
    } else if (this.isOnHomePage && !atTop && this.placeholderIntervalId) {
      this.stopPlaceholderAnimation();
    }
  }
  startPlaceholderAnimation() {
    this.placeholderIntervalId = setInterval(() => {
      this.triggerPlaceholderRoll();
    }, PLACEHOLDER_DISPLAY_DURATION);
  }
  stopPlaceholderAnimation() {
    if (this.placeholderIntervalId) {
      clearInterval(this.placeholderIntervalId);
      this.placeholderIntervalId = null;
    }
  }
  triggerPlaceholderRoll() {
    this.isRolling = true;
    setTimeout(() => {
      this.currentPlaceholderIndex = (this.currentPlaceholderIndex + 1) % this.placeholderLabels.length;
      this.isRolling = false;
    }, PLACEHOLDER_ANIMATION_DURATION);
  }
  get showAnimatedPlaceholder() {
    return this.isAgenticSearchEnabled && !this.keyword && !this.isInputFocused && this.placeholderLabels.length > 0;
  }
  getCurrentLanguage() {
    return selectCurrentLanguage(i18nStore.getState());
  }
  get currentPlaceholder() {
    return this.placeholderLabels[this.currentPlaceholderIndex];
  }
  get nextPlaceholder() {
    const nextIndex = (this.currentPlaceholderIndex + 1) % this.placeholderLabels.length;
    return this.placeholderLabels[nextIndex];
  }
  get animatedPlaceholderClass() {
    return this.isRolling ? 'placeholder-roller rolling' : 'placeholder-roller';
  }
  get glowWrapperClass() {
    return this.isAgenticSearchEnabled ? 'input-glow-wrapper agentic-glow' : 'input-glow-wrapper';
  }
  get barContainerClass() {
    return this.isAgenticSearchEnabled ? 'bar-container agentic-search' : 'bar-container';
  }
  get searchBarLabel() {
    if (this.labels?.searchBar) {
      return this.labels.searchBar.replace('AppExchange', 'AgentExchange');
    }
    return this.labels?.searchBar || '';
  }
  get inputPlaceholder() {
    return this.showAnimatedPlaceholder ? '' : this.searchBarLabel;
  }
  async setKeywordFromParamsAndUrl(info) {
    // Repeated params (`?keywords=a&keywords=b`) parse into an array, so `keywords` is only
    // sometimes a string despite its type. Treat any other shape as if the param were absent.
    if (typeof info.params?.keywords === 'string' && info.params.keywords && info.url.includes(Routes.SEARCH)) {
      const keyword = sanitizeText(info.params.keywords);
      this.keyword = keyword.trim().slice(0, this.maxSearchCharLimit);
    } else {
      this.keyword = '';
    }
    this.toggleDisplayDrawer(false);
    this.blurInput();
  }
  async setSearchResults(searchState) {
    if (searchState) {
      this.setSearchTermSuggestions(this.getCurrentLanguage() === ENGLISH_LANGUAGE_CODE ? selectSearchTermSuggestions(searchState, this.keyword) ?? [] : []);
    }
  }
  async setRecentItemsResults(recentItemState) {
    if (recentItemState) {
      this.setRecentItems(selectRecentItems(recentItemState));
    }
  }
  async setTopSearchesResults(topSearchesState) {
    if (topSearchesState) {
      this.topSearches = selectTopSearches(topSearchesState);
    }
  }

  // @ts-expect-error * Automated Type Error Fix
  handleTextInput(event) {
    if (this.isNewKeyword(event.target.value)) {
      this.doItemSearch(event.target.value, true);
      this.unfocusActiveElement();
    }
  }

  // @ts-expect-error * Automated Type Error Fix
  handleInputKeyUp(event) {
    if (event.key === 'Enter') {
      const focusedOption = this.getFocusedItem();
      if (focusedOption) {
        this.handleOptionSelected(focusedOption);
      } else {
        this.doSearch(this.keyword);
      }
    }
  }

  // @ts-expect-error * Automated Type Error Fix
  isNewKeyword(keyword) {
    return keyword !== this.keyword || !this.searchTermSuggestions?.length;
  }

  // @ts-expect-error * Automated Type Error Fix
  handleInputKeyDown(event) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      // Prevent the page from scrolling, when navigating options with arrow keys
      this.toggleDisplayDrawer(true);
      event.preventDefault();
      const suggestions = this.template.querySelectorAll('.list-option');
      if (suggestions.length) {
        const focusedElementIdx = this.getFocusedItemIndex(suggestions);
        if (event.key === 'ArrowDown') {
          this.moveFocus(focusedElementIdx, suggestions, 0, FocusDirection.Down);
        } else {
          this.moveFocus(focusedElementIdx, suggestions, suggestions.length - 1, FocusDirection.Up);
        }
      }
    }
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      this.unfocusActiveElement();
    }
    if (event.key === 'Escape') {
      this.toggleDisplayDrawer(false);
    }
  }
  getInputElement() {
    // @ts-expect-error * Automated Type Error Fix
    return this.template.querySelector('.bar-input');
  }

  // @ts-expect-error * Automated Type Error Fix
  handleOptionMouseOver(event) {
    const focusedElement = this.getFocusedItem();
    const nextElement = event.currentTarget;
    const inputElement = this.getInputElement();
    if (focusedElement) {
      this.unfocusElement(inputElement, focusedElement);
    }
    if (nextElement) {
      this.focusElement(inputElement, nextElement);
    }
  }

  // @ts-expect-error * Automated Type Error Fix
  handleBackdropMouseOver(_event) {
    this.unfocusActiveElement();
  }

  // @ts-expect-error * Automated Type Error Fix
  handleCloseKeyDown(event) {
    if (event.key === 'Enter') {
      this.handleCloseIconClick();
    }
  }

  // @ts-expect-error * Automated Type Error Fix
  handleContainerFocusOut(event) {
    const isFocusWithinTypeahead = event.relatedTarget && this.template.contains(event.relatedTarget);
    if (!isFocusWithinTypeahead) {
      this.toggleDisplayDrawer(false);
      this.blurInput();
    }
  }
  getFocusedItemIndex(suggestions) {
    return Array.from(suggestions).findIndex(s => s.classList.contains('active'));
  }
  getFocusedItem() {
    // @ts-expect-error * Automated Type Error Fix
    return this.template.querySelector('.list-option.active');
  }
  moveFocus(focusedElementIdx, suggestions, startPosition, direction) {
    const inputElement = this.getInputElement();
    const focusedElement = suggestions.item(focusedElementIdx);
    if (focusedElement) {
      const nextElement = suggestions.item(focusedElementIdx + direction);
      this.unfocusElement(inputElement, focusedElement);
      if (nextElement) {
        this.focusElement(inputElement, nextElement);
      }
    } else {
      const firstElement = suggestions.item(startPosition);
      this.focusElement(inputElement, firstElement);
    }
  }
  unfocusElement(inputElement, focusedElement) {
    focusedElement.classList.remove('active');
    focusedElement.setAttribute('aria-selected', 'false');
    inputElement.setAttribute('aria-activedescendant', '');
  }
  focusElement(inputElement, nextElement, shouldScroll = true) {
    nextElement.classList.add('active');
    nextElement.setAttribute('aria-selected', 'true');
    inputElement.setAttribute('aria-activedescendant', nextElement.id);
    if (shouldScroll) {
      nextElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'start'
      });
    }
  }
  unfocusActiveElement() {
    const inputElement = this.getInputElement();
    const focusedElement = this.getFocusedItem();
    if (inputElement && focusedElement) {
      this.unfocusElement(inputElement, focusedElement);
    }
  }

  // @ts-expect-error * Automated Type Error Fix
  doItemSearch(newKeyword, debounceRequired) {
    this.keyword = newKeyword;
    if (this.keywordPresent()) {
      // Reset top searches when a keyword is entered
      if (this.topSearches.length > 0) {
        topSearchesStore.reset();
      }
      if (debounceRequired) {
        this.debounceTimer = debounce(this, this.doSuggestionsSearch, [], this.debounceTimer, debounceTimeout);
      } else {
        this.doSuggestionsSearch();
      }
    } else {
      this.doRecentItemsSearch();
      if (this.getCurrentLanguage() === ENGLISH_LANGUAGE_CODE) {
        this.doTopSearchesSearch();
      }
    }
    this.toggleDisplayDrawer(true);
  }

  // @ts-expect-error * Automated Type Error Fix
  handleInputFocus(event) {
    this.isInputFocused = true;
    this.doItemSearch(event.target.value, false);
    this.toggleDisplayDrawer(true);
  }
  handleInputBlur(event) {
    const isFocusWithinTypeahead = event.relatedTarget && this.template.contains(event.relatedTarget);
    if (!isFocusWithinTypeahead) {
      this.isInputFocused = false;
    }
  }
  setRecentItems(newRecentItems) {
    this.recentItems = newRecentItems?.map((recentItem, index) => ({
      ...recentItem,
      recentItemId: `recent-item-${index}`
    }));
  }
  doRecentItemsSearch() {
    const recentItemsInState = selectRecentItems(recentItemStore.getState());
    if (recentItemsInState && recentItemsInState.length) {
      this.setRecentItems(recentItemsInState);
    } else {
      recentItemStore.fetchRecentItems(this.userId);
    }
  }
  doTopSearchesSearch() {
    topSearchesStore.fetchTopSearches();
  }
  async doSuggestionsSearch() {
    if (this.getCurrentLanguage() !== ENGLISH_LANGUAGE_CODE) {
      this.setSearchTermSuggestions([]);
      return;
    }
    if (this.keywordPresent() && this.validateKeyword(this.keyword)) {
      const keyword = this.keyword;
      const suggestions = await selectSearchTermSuggestionsFromTrie(searchTermSuggestionStore.getState(), keyword);
      if (keyword === this.keyword) {
        this.setSearchTermSuggestions(suggestions);
      }
    } else {
      this.setSearchTermSuggestions([]);
    }
  }
  get shouldShowSearchSuggestions() {
    return this.keywordPresent() && this.searchTermSuggestions.length > 0;
  }
  get shouldShowRecentItems() {
    return this.recentItems && this.recentItems.length && !this.shouldShowSearchSuggestions;
  }
  get shouldShowTopSearches() {
    return this.getCurrentLanguage() === ENGLISH_LANGUAGE_CODE && this.topSearches.length && !this.shouldShowSearchSuggestions;
  }
  get shouldDisplayDrawer() {
    // @ts-expect-error * Automated Type Error Fix
    return this.displayDrawer && (this.shouldShowRecentItems || this.shouldShowSearchSuggestions || this.shouldShowTopSearches);
  }
  get shouldDisplayDrawerStyle() {
    if (this.shouldDisplayDrawer) {
      return '';
    }
    return 'display:none';
  }
  get drawerContainerClasses() {
    if (this.isAgenticSearchEnabled) {
      return 'drawer-container-padding drawer-content';
    }
    return 'drawer-content';
  }
  keywordPresent() {
    return this.keyword && this.keyword.length > 0;
  }
  get showActionButtons() {
    return this.isAgenticSearchEnabled;
  }
  get showSendButton() {
    return this.isInputFocused && Boolean(this.keywordPresent());
  }
  handleCloseIconClick() {
    this.keyword = '';
  }
  handleSendButtonClick() {
    this.doSearch(this.keyword);
    this.isInputFocused = false;
  }
  handleSendButtonMouseDown(event) {
    event.preventDefault();
  }
  doSearch(keyword, searchSource) {
    if (keyword) {
      if (keyword.trim() !== '') {
        this.keyword = keyword.trim();
        if (!searchSource) {
          searchTermSuggestionStore.setInlineSuggestions(this.searchTermSuggestions);
        }
        const encodedKeyword = encodeURIComponent(this.keyword);
        const isJpPath = siteConfigStore.getState().isAgentExchange && this.getCurrentLanguage() === JAPANESE_LANGUAGE_CODE;
        const basePath = isJpPath ? '/jp' : '';
        const url = searchSource ? `${basePath}/appxSearchKeywordResults?keywords=${encodedKeyword}&searchSource=${encodeURIComponent(searchSource)}` : `${basePath}/appxSearchKeywordResults?keywords=${encodedKeyword}`;
        navigate(url);
      } else {
        this.keyword = '';
      }
    }
    this.toggleDisplayDrawer(false);
    this.blurInput();
  }
  static getSearchSource(type) {
    return type === 'topSearch' ? 'topSearch' : 'autoSuggestion';
  }

  // @ts-expect-error * Automated Type Error Fix
  handleSuggestionClick(event) {
    const {
      searchValue,
      type
    } = event.currentTarget.dataset;
    this.handleSuggestionSearch(searchValue, TypeAhead.getSearchSource(type));
  }

  // @ts-expect-error * Automated Type Error Fix
  handleSuggestionSearch(searchKeyword, searchSource = 'autoSuggestion') {
    searchTermSuggestionStore.setInlineSuggestions(this.searchTermSuggestions);
    this.doSearch(searchKeyword, searchSource);
  }

  // @ts-expect-error * Automated Type Error Fix
  handleRecentItemClick(event) {
    this.handleRecentItemSearch(event.currentTarget.dataset.searchValue);
  }

  // @ts-expect-error * Automated Type Error Fix
  handleRecentItemSearch(listingId) {
    if (siteConfigStore.getState().isAgentExchange) {
      window.location.assign(`${APPX_SITE_URL}/${Routes.LISTING}?listingId=${listingId}`);
    } else {
      navigate(`/${Routes.LISTING}?listingId=${listingId}`);
    }
    this.toggleDisplayDrawer(false);
    this.blurInput();
  }
  handleBackdropClick() {
    this.toggleDisplayDrawer(false);
    this.blurInput();
  }
  toggleDisplayDrawer(bool) {
    this.displayDrawer = bool;
  }
  setSearchTermSuggestions(newSearchSuggestions) {
    this.searchTermSuggestions = newSearchSuggestions?.map((suggestion, index) => ({
      ...suggestion,
      suggestionId: `suggestion-${index}`
    })) ?? [];
  }

  // @ts-expect-error * Automated Type Error Fix
  handleOptionSelected(option) {
    const {
      type,
      searchValue
    } = option.dataset;
    if (type === 'recentItem') {
      this.handleRecentItemSearch(searchValue);
    } else {
      this.handleSuggestionSearch(searchValue, TypeAhead.getSearchSource(type));
    }
  }
  get keywordSuggestion() {
    const suggestedKeyword = this.keyword ? this.keyword.trim() : '';
    return {
      suggestedKeyword: suggestedKeyword,
      highlighted: null,
      suggestionId: 'suggestion-keyword',
      searchKeyword: suggestedKeyword
    };
  }
  get topSearchesWithIds() {
    return this.topSearches.map((search, index) => ({
      suggestedKeyword: search,
      highlighted: search,
      suggestionId: `top-search-${index}`
    }));
  }
  blurInput() {
    this.getInputElement()?.blur();
  }
  validateKeyword(keyword) {
    return keyword.replace(/\s/g, '').length >= MIN_CHARACTERS_BEFORE_RETURNING_RESULTS;
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(TypeAhead, {
  track: {
    currentPlaceholderIndex: 1,
    isRolling: 1,
    searchTermSuggestions: 1,
    recentItems: 1,
    topSearches: 1
  },
  wire: {
    labels: {
      adapter: WireI18n,
      dynamic: [],
      config: function ($cmp) {
        return {
          selector: selectTypeAhead
        };
      }
    },
    setKeywordFromParamsAndUrl: {
      adapter: WireStore,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          store: routerStore,
          selector: state => ({
            params: selectParams(state),
            url: selectUrl(state)
          })
        };
      }
    },
    setSearchResults: {
      adapter: WireStore,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          store: searchTermSuggestionStore
        };
      }
    },
    setRecentItemsResults: {
      adapter: WireStore,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          store: recentItemStore
        };
      }
    },
    setTopSearchesResults: {
      adapter: WireStore,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          store: topSearchesStore
        };
      }
    }
  },
  fields: ["keyword", "userId", "displayDrawer", "maxSearchCharLimit", "isInputFocused", "boundScrollHandler"]
});
const __lwc_component_class_internal = _registerComponent(TypeAhead, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;